import api from "lib/axios";
import type { ApiEnvelope, PaginatedResult } from "services/apiTypes";
import { unwrapData, unwrapList } from "services/apiTypes";
import {
  unwrapAvailableCoursesResult,
  type StudentAvailableCourse,
} from "lib/instructorStudentCourseFilter";

export type { StudentAvailableCourse };

export type InstructorStudentProfile = {
  parent_phone?: string | null;
  gender?: string | null;
  date_of_birth?: string | null;
  stage_id?: number | null;
  grade_id?: number | null;
  stage?: { id: number; name: string; slug?: string } | null;
  grade?: { id: number; name: string } | null;
};

export type InstructorStudent = {
  id: number;
  name: string | null;
  phone?: string | null;
  image?: string | null;
  image_url?: string | null;
  is_active?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  profile?: InstructorStudentProfile | null;
  can_view?: boolean;
  created_by_you?: boolean;
  enrolled_course_ids?: number[];
  enrolled_courses?: InstructorStudentCourseSummary[];
};

export type InstructorStudentCourseSummary = {
  id: number;
  title: string;
  image?: string | null;
  subject_id?: number | null;
  start_date?: string | null;
  end_date?: string | null;
};

export type InstructorStudentDetails = InstructorStudent & {
  can_view: boolean;
  courses_with_you: InstructorStudentCourseSummary[];
  note: string | null;
  tags: string[];
};

export type StudentsListQuery = {
  page?: number;
  limit?: number;
  term?: string;
  course_id?: number;
};

export type CreateInstructorStudentBody = {
  name: string;
  phone: string;
  parent_phone?: string | null;
  gender: "male" | "female";
  date_of_birth: string;
  stage_id?: number;
  grade_id?: number | null;
};

export type EnrollStudentBody = {
  start_date: string;
  end_date: string;
  price_paid?: number | null;
};

export const instructorStudentsService = {
  async list(params: StudentsListQuery = {}) {
    const res = await api.get<ApiEnvelope<PaginatedResult<InstructorStudent>>>(
      "/instructors/students",
      { params, meta: { toastSuccess: false } }
    );
    return unwrapList<InstructorStudent>(res.data);
  },

  async get(studentId: number | string) {
    const res = await api.get<ApiEnvelope<{ data: InstructorStudentDetails }>>(
      `/instructors/students/${studentId}`,
      { meta: { toastSuccess: false } }
    );
    return unwrapData<InstructorStudentDetails>(res.data);
  },

  async listAvailableCourses(studentId: number | string) {
    const res = await api.get<ApiEnvelope<StudentAvailableCourse[] | { data: StudentAvailableCourse[] }>>(
      `/instructors/students/${studentId}/available-courses`,
      { meta: { toastSuccess: false } }
    );
    return unwrapAvailableCoursesResult(res.data?.result);
  },

  async unenroll(studentId: number | string, courseId: number | string) {
    const res = await api.delete<ApiEnvelope<unknown>>(
      `/instructors/students/${studentId}/courses/${courseId}`
    );
    return { message: (res.data as any)?.message ?? "Removed." };
  },

  async saveNote(studentId: number | string, note: string | null) {
    const res = await api.put<ApiEnvelope<{ data: { note: string | null; tags: string[] } }>>(
      `/instructors/students/${studentId}/note`,
      { note }
    );
    return unwrapData<{ note: string | null; tags: string[] }>(res.data);
  },

  async saveTags(studentId: number | string, tags: string[]) {
    const res = await api.put<ApiEnvelope<{ data: { note: string | null; tags: string[] } }>>(
      `/instructors/students/${studentId}/tags`,
      { tags }
    );
    return unwrapData<{ note: string | null; tags: string[] }>(res.data);
  },
};
